import React, { useEffect, useMemo, useState } from "react";

export default function WalletOverview() {
  const [walletData, setWalletData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  async function fetchWallet() {
    setLoading(true);
    setErr("");
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/wallet/`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to load wallet");
      setWalletData(data);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchWallet();
  }, []);

  const hasPending = useMemo(() => {
    const txPending = (walletData?.recentTransactions || []).some((t) => t.status === "pending");
    const trPending = (walletData?.recentTransfers || []).some((t) => t.status === "pending");
    return txPending || trPending;
  }, [walletData]);

  function labelStatus(s) {
    if (s === "pending") return "En attente (validation admin)";
    if (s === "completed") return "Confirmé";
    if (s === "failed") return "Rejeté";
    if (s === "cancelled") return "Annulé";
    return s || "—";
  }

  function fmtMoney(amount) {
    if (amount === null || amount === undefined || amount === "") return "—";
    const n = Number(amount);
    if (!Number.isFinite(n)) return `${amount} CAD`;
    return `${n.toFixed(2)} CAD`;
  }

  return (
    <section className="rounded-2xl border border-black/5 dark:border-white/10 bg-white dark:bg-slate-900 shadow-sm p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold text-slate-900 dark:text-white">Aperçu du wallet</h2>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Solde + dernières opérations</p>
        </div>

        <button
          onClick={fetchWallet}
          className="rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-slate-900 px-4 py-2 text-sm font-medium
                     text-slate-900 dark:text-white hover:bg-black/5 dark:hover:bg-white/10 transition"
        >
          Rafraîchir
        </button>
      </div>

      {loading && <Skeleton />}

      {!loading && err && (
        <div className="mt-4 rounded-2xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-700 dark:text-red-300">
          {err}
        </div>
      )}

      {!loading && walletData && (
        <div className="mt-5 space-y-6">
          {hasPending && (
            <div className="rounded-2xl border border-amber-500/20 bg-amber-500/10 px-4 py-3 text-sm text-amber-800 dark:text-amber-200">
              Certaines opérations sont en attente de validation administrateur.
            </div>
          )}

          <div className="rounded-2xl border border-black/5 dark:border-white/10 bg-slate-50 dark:bg-white/5 p-4">
            <div className="text-sm text-slate-500 dark:text-slate-400">Solde (CAD)</div>

            <div className="mt-1 text-2xl font-semibold text-slate-900 dark:text-white">
              {fmtMoney(walletData.wallet?.balanceCad)}
            </div>

            <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Held: {fmtMoney(walletData.wallet?.heldCad)} • Available: {fmtMoney(walletData.wallet?.availableCad)}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Block title="Transactions récentes">
              {(walletData.recentTransactions || []).length === 0 ? (
                <Empty text="Aucune transaction récente" />
              ) : (
                <div className="space-y-2">
                  {walletData.recentTransactions.map((tx) => (
                    <Row
                      key={tx.id}
                      left={`${tx.type}`}
                      right={fmtMoney(tx.amount)}
                      sub={`${labelStatus(tx.status)} • ${new Date(tx.createdAt).toLocaleString()}`}
                    />
                  ))}
                </div>
              )}
            </Block>

            <Block title="Transferts récents">
              {(walletData.recentTransfers || []).length === 0 ? (
                <Empty text="Aucun transfert récent" />
              ) : (
                <div className="space-y-2">
                  {walletData.recentTransfers.map((tr) => (
                    <Row
                      key={tr.id}
                      left={`À: ${tr.recipientName}`}
                      right={fmtMoney(tr.amount)}
                      sub={`${labelStatus(tr.status)} • ${tr.recipientPhone}`}
                    />
                  ))}
                </div>
              )}
            </Block>
          </div>
        </div>
      )}
    </section>
  );
}

function Block({ title, children }) {
  return (
    <div className="rounded-2xl border border-black/5 dark:border-white/10 bg-white dark:bg-slate-950 p-4">
      <h3 className="text-sm font-semibold text-slate-900 dark:text-white">{title}</h3>
      <div className="mt-3">{children}</div>
    </div>
  );
}

function Row({ left, right, sub }) {
  return (
    <div className="rounded-2xl border border-black/5 dark:border-white/10 bg-slate-50 dark:bg-white/5 px-4 py-3 flex items-start justify-between gap-4">
      <div className="min-w-0">
        <div className="text-sm font-medium text-slate-900 dark:text-white truncate">{left}</div>
        <div className="text-xs text-slate-500 dark:text-slate-400">{sub}</div>
      </div>
      <div className="text-sm font-semibold text-slate-900 dark:text-white whitespace-nowrap">{right}</div>
    </div>
  );
}

function Empty({ text }) {
  return (
    <div className="rounded-2xl border border-dashed border-black/10 dark:border-white/10 p-5 text-center">
      <p className="text-sm text-slate-500 dark:text-slate-400">{text}</p>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="mt-4 space-y-3 animate-pulse">
      <div className="h-16 rounded-2xl bg-black/5 dark:bg-white/10" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="h-40 rounded-2xl bg-black/5 dark:bg-white/10" />
        <div className="h-40 rounded-2xl bg-black/5 dark:bg-white/10" />
      </div>
    </div>
  );
}
